import { bd09toGcj02, gcj02ToBd09 } from '@/utils/coordinateTransform'
import Tracker from '@nio-fe/nio-tracker-web'
import { Store } from 'pinia'
import { PropType, computed, defineComponent, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getLocation } from '../../apis/map.api'
import { DEFAULT_DISTRICT_ZOOM_LEVEL } from '../../config/zoomLevelConfig'
import { MapViewMode, Point } from '../../contract/map'
import { LocationModel } from '../../models/location.model'
import { ComponentActions, ComponentState } from '../../nsh5/localStores/component'
import { useAppStore } from '../../stores/app'
import { ChargeMapContext } from '../chargerMap/index.model'
import { addResourceCircle, flyTo, removeResourceCircle } from '../chargerMap/mapUtils'
import { PowerResource } from '../iconRender/icon.model'
import { IPin } from './pin.model'
import { getSwapAroundSelectedLoc } from './utils'

/**
 * 戳点渲染功能模块
 */
export default defineComponent({
  name: 'pin-render-view',
  emits: ['pinMarkerClick'],
  props: {
    mapContextRef: {
      type: Object as PropType<ChargeMapContext>,
    },
    mapInstance: {
      type: Object,
    },
    BMapGL: {
      type: Object,
    },
    componentStore: {
      // 用于同一项目功能模块间的通信
      type: Object as PropType<Store<string, ComponentState, {}, ComponentActions>>,
      default: null,
    },
  },

  setup: (props, { emit, expose }) => {
    const mapContextRef = computed(() => props.mapContextRef)
    const mapInstance = computed(() => props.mapInstance)
    const BMapGL = computed(() => props.BMapGL)
    // const mapContextRef = ref<ChargeMapContext>()
    // const mapInstance = ref<any>()
    const zoomLevel = ref<number>(0)
    const route = useRoute()
    const router = useRouter()
    let currentPinMarker = null as any // 戳点
    let loc: Point // bd09
    let gcjLoc: Point // gcj
    let searchLocName: string
    let searchLocAddress: string
    let pinPowerAroundTimestamp: number
    let swaps: PowerResource[] = [] // pin点周围换电站支援
    let locationInfo: LocationModel
    let resourceCircleIds: string[] = [] // 电区房id集合
    const appStore = useAppStore()
    let pointXY = '' // 记录点击点 防止多次点击

    /**
     * 画Pin点
     * @param loc
     * @param type
     */
    const addPinMarker = () => {
      getLocationInfo() // 获取当前戳点位置信息
      getAroundCircle() // 画pin点周围的电区房
      setMarkerFn() // 设置pin点
      // changeRouteFn() // 改变路由
    }

    /**
     * 获取当前位置信息
     */
    const getLocationInfo = async () => {
      const res = await getLocation(gcjLoc, 'pinRender') // gcj
      if (res.isValid()) {
        locationInfo = res.data
        searchLocName && (locationInfo.name = searchLocName)
        searchLocAddress && (locationInfo.address = searchLocAddress)
        changeRouteFn() // 改变路由
        // 埋点-城市地图页面地图戳点
        Tracker.track(
          'nsh5_citymapclick',
          Object.assign({}, appStore.trackParamsCommon, {
            poicityname: locationInfo?.city,
            poiaddress: locationInfo?.address,
            poiname: locationInfo?.name,
          })
        )
      }
    }

    /**
     * 画pin点周围的电区房
     */
    const getAroundCircle = async () => {
      removeResourceCircle(mapContextRef.value!)
      const requestTime = new Date().getTime()
      pinPowerAroundTimestamp = requestTime
      swaps = await getSwapAroundSelectedLoc(gcjLoc!, zoomLevel.value, 3000) // gcj
      resourceCircleIds = swaps.map((a) => a.id)
      if (swaps.length > 0 && requestTime == pinPowerAroundTimestamp) {
        addResourceCircle(mapContextRef.value!, swaps)
      }
    }

    /**
     * 画Pin点
     */
    const setMarkerFn = () => {
      const marker: IPin = {
        id: loc.lng + ',' + loc.lat,
        loc: loc, // bd
        size: { width: 44, height: 44 },
        icon: (process.config as any).pin,
        zIndex: 99,
      }
      setLocationMarker(marker)
    }

    /**
     *
     */
    const changeRouteFn = async () => {
      const query: any = {
        location: gcjLoc.lng + ',' + gcjLoc.lat,
      }
      searchLocName && (query['name'] = searchLocName)
      searchLocAddress && (query['address'] = searchLocAddress)
      if (route.name == 'home') {
        searchLocName = ''
        searchLocAddress = ''
        window.backHistoryNum++
        await router.push({
          name: 'pin',
          query,
          state: {
            backHistoryNum: window.backHistoryNum,
          },
        })
      } else {
        await router.replace({
          name: 'pin',
          query: {
            location: gcjLoc.lng + ',' + gcjLoc.lat,
          },
          state: {
            backHistoryNum: window.backHistoryNum,
          },
        })
      }
      // 再次点击marker点需要显示气泡
      props.componentStore.setDestLocationByPin({
        name: locationInfo?.name,
        lng: loc.lng,
        lat: loc.lat,
        address: locationInfo?.address,
      }) // 重新设置destLocation
      props.componentStore.$patch({
        cardLoaded: true,
      })
    }

    props.componentStore.bus.on('clearLocationMarker', () => {
      clearLocationMarker()
    })
    /**
     * 清除pin点
     */
    const clearLocationMarker = () => {
      mapInstance.value?.removeOverlay(currentPinMarker)
      props.componentStore.unsetDestLocationByPin() // 关闭气泡
    }

    /**
     * 设置pin点
     * @param marker
     */
    const setLocationMarker = (pin: IPin) => {
      clearLocationMarker()
      try {
        const svgUrl = pin.icon
        const w = pin.size?.width || 30
        const h = pin.size?.height || 30
        const myIcon = new BMapGL.value!.Icon(svgUrl, new BMapGL.value!.Size(w, h))
        const point = new BMapGL.value!.Point(pin.loc.lng, pin.loc.lat)
        const pinMarker = new BMapGL.value!.Marker(point, {
          icon: myIcon,
          enableDragging: false,
          offset: new BMapGL.value!.Size(0, -h / 2),
        })
        pinMarker.customData = {
          marker: { clickable: true }, // 点击pin点不触发touchend事件
        }
        // pinMarker.disableMassClear()
        pinMarker.setZIndex(pin.zIndex || 1)
        mapInstance.value?.addOverlay(pinMarker)
        pinMarker.addEventListener('click', pinMarkerClick, false)
        currentPinMarker = pinMarker
      } catch (error) {
        console.log(`================出错了: ${error}====================`)
      }
    }

    const pinMarkerClick = (e: any) => {
      // 搜索场景下搜索框内有结果, 重新点击pin点
      props.componentStore.bus.emit('clearBackList') // 根据搜索条件，清空后退列表

      if (swaps.length && locationInfo) {
        addResourceCircle(mapContextRef.value!, swaps)
      }
      changeRouteFn()
      emit('pinMarkerClick', e?.currentTarget?.latLng)
    }

    props.componentStore.bus.on('selectedPin', () => {
      props.componentStore.$patch({
        cardLoaded: false,
      })
      // route from pin
      // 异步，保证route变化后再执行
      getUrlParams()
    })

    /**
     * 用于直接进入页面时，获取url参数，然后跳转到对应的点
     */
    const getUrlParams = async (backHome = true) => {
      const qLocation = route.query.location
      if (route.name == 'pin' && qLocation) {
        searchLocName = route.query.name as string
        searchLocAddress = route.query.address as string
        if (backHome) {
          // 用于后退重置为首页
          window.backHistoryNum = 0
          await router.replace({
            name: 'home',
            state: {
              backHistoryNum: window.backHistoryNum,
            },
          }) // 为了保证回退时回到首页
          // router.replace('/') // 为了保证回退时回到首页
        }
        const [lng, lat] = (qLocation as string).split(',') // bd
        loc = gcj02ToBd09({
          lng: Number(lng),
          lat: Number(lat),
        }) // bd09
        gcjLoc = {
          lng: Number(lng),
          lat: Number(lat),
        }
        props.componentStore.bus.emit('preventZoomDragAction', 3000)
        flyTo(mapContextRef.value!, loc, 13)
        addPinMarker()
        // setTimeout(() => {
        //   getAroundCircle()
        // }, 1000)
      }
    }

    props.componentStore.bus.on('backPinRoute', () => {
      getUrlParams(false)
    })

    /**
     * 筛选改变
     */
    const pinFilterChanged = () => {
      if (route.name == 'pin') {
        removeResourceCircle(mapContextRef.value!) // 没有换电站，清除电区房
        if (props.componentStore.getPowerSwapInFilter()) {
          // 有换电站，重画
          if (swaps.length > 0) {
            addResourceCircle(mapContextRef.value!, swaps)
          }
        }
      }
    }
    props.componentStore.bus.on('filterChanged', pinFilterChanged)

    /**
     * 切换Pin点大小
     * @param isBig
     */
    const changePinSize = (isBig: boolean) => {
      const size = isBig ? 44 : 30
      currentPinMarker?.setIcon(
        new BMapGL.value!.Icon((process.config as any).pin, new BMapGL.value!.Size(size, size))
      )
      currentPinMarker?.setOffset(new BMapGL.value!.Size(0, -size / 2))
    }

    // ------------暴露出去的方法--------------
    const zoomEnd = async () => {
      zoomLevel.value = mapInstance.value?.getZoom()
    }
    const touchEnd = (e: any) => {
      if (
        props.componentStore.tempSearchPoiResource &&
        props.componentStore.tempSearchPoiResource.name
      ) {
        // 搜索场景下搜索框内有结果, 不允许点击戳点
        return
      }
      // 不允许双击
      if (pointXY == e.point.lng + ',' + e.point.lat) {
        return
      } else {
        pointXY = e.point.lng + ',' + e.point.lat
      }
      if (props.componentStore.viewMode === MapViewMode.Init) {
        // marker可点击的情况下忽略touchend事件
        if (
          e.overlay?.customData?.marker?.clickable ||
          zoomLevel.value <= DEFAULT_DISTRICT_ZOOM_LEVEL
        ) {
          return
        }
        // 搜索状态下无法触发戳点事件 不用考虑
        // 非搜索状态下，点击地图，清空回退记录
        props.componentStore.bus.emit('forceClearBackList') // 清空后退列表
        loc = {
          lng: e.latlng.lng,
          lat: e.latlng.lat,
        } as Point // bd
        gcjLoc = bd09toGcj02(loc) // gcj
        addPinMarker()
      }
    }
    expose({
      zoomEnd,
    })

    // 启动相关事件监听
    const initEventListener = () => {
      mapInstance.value?.addEventListener('zoomend', zoomEnd, false)
      mapInstance.value?.addEventListener('click', touchEnd, false)
    }

    // // unmounted时摧毁相关事件监听
    // const destroyEventListener = () => {
    //   mapInstance.value?.removeEventListener('zoomend', zoomEnd, false)
    //   mapInstance.value?.removeEventListener('click', touchEnd, false)
    // }

    onUnmounted(() => {
      // destroyEventListener()
      props.componentStore.bus.off('backPinRoute')
      props.componentStore.bus.off('clickedMarker')
      props.componentStore.bus.off('selectedPin')
      props.componentStore.bus.off('filterChanged', pinFilterChanged)
      props.componentStore.bus.off('clearLocationMarker')
    })

    /**
     * 组件获取地图实例后的回调
     */
    const onReady = () => {
      // BMapGL = window.BMapGL
      // mapInstance.value = mapContextRef.value?.getMap()
      initEventListener()
      getUrlParams() // 读取url参数，用于直接通过url进入页面
    }

    watch(
      () => props.mapContextRef,
      () => {
        if (props.mapContextRef) {
          // mapContextRef.value = props.mapContextRef
          onReady()
        }
      },
      {
        immediate: true,
      }
    )

    // --------------------------
    watch(
      () => zoomLevel.value,
      () => {
        if (zoomLevel.value <= DEFAULT_DISTRICT_ZOOM_LEVEL) {
          // pin点存在情况下（pin/resource），缩放到小于城市级别，清除pin点， 同时修改路由
          clearLocationMarker()
          removeResourceCircle(mapContextRef.value!)
          if (route.name == 'pin') {
            router.replace({
              name: 'home',
            })
          }
        }
      }
    )
    watch(
      () => route.name,
      (newVal, oldVal) => {
        changePinSize(newVal === 'pin')
        if (newVal === 'pin' && oldVal === 'plan') {
          // 从路径规划页返回会有一个fly过程，延迟500ms再画pin点
          // 不延迟的话会被watch 里面的zoom直接清除
          setTimeout(() => {
            props.componentStore.setDestLocationByPin({
              name: locationInfo?.name,
              lng: loc.lng,
              lat: loc.lat,
              address: locationInfo?.address,
            }) // 重新设置destLocation
            mapInstance.value?.addOverlay(currentPinMarker) // 显示pin点
            addResourceCircle(mapContextRef.value!, swaps) // 显示周围的电区房
          }, 500)
          return
        }
        if (newVal !== 'pin' && oldVal === 'pin') {
          removeResourceCircle(mapContextRef.value!, resourceCircleIds)
          props.componentStore.unsetDestLocationByPin() // 去除气泡
          return
        }
      }
    )
    return () => {}
  },
})
